import React, { useContext, useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Counter from "../../../../shared/components/form/Counter";
import { FlexContainer } from "../../../../shared/components/styled/Container.styled";
import FormItemDropdown from "../../../../shared/components/form/FormItemDropdown";
import FormItemInput from "../../../../shared/components/form/FormItemInput";
import {
  createDaysDropdownValues as createOrdinalDayOptions,
} from "../../../../shared/utils/utility-functions";
import { ConfiguratorContext } from "../../../../contexts/ConfiguratorContext";
import { useIsMount } from "../../../../shared/hooks/useIsMount";

// These ranges count days elapsed since the loan's due date, so the dropdowns list
// plain counts. The shared helper labels its values as ordinals ("1st", "14th")
// because its other callers are picking days of the month, which reads here as a
// calendar date and is what made the option look like a month schedule.
const createDaysDropdownValues = (minDay, maxDay, key) =>
  createOrdinalDayOptions(minDay, maxDay, key).map((option) => ({
    ...option,
    label: `${option.value}`,
  }));

// Comfortably clears three ranges plus the caption and any validation messages.
const DRAWER_MAX_HEIGHT = "420px";

// The last range runs to the end of the month, and the stored format needs a number
// for its upper bound. Both the portal and the service parse this as a plain count of
// days past the due date, so a borrower further past due than this matches no range at
// all and is charged nothing. That is the intended reading: the ranges describe a
// single billing month, not open-ended delinquency.
const LAST_RANGE_END_DAY = 31;

// The days sit in a fixed-width column so the colons line up down the card rather
// than tracking the end of each range's wording.
const DAYS_COLUMN_WIDTH = "190px";
const FEE_INPUT_WIDTH = "80px";
const DAY_DROPDOWN_WIDTH = "56px";

const DateRangeFeeOption = ({
  elementKey = "",
  selected = false,
  numberOfDateRanges = 0,
  setNumberOfDateRanges = () => {},
  onChangeHandler = () => {},
  validateInputValue = () => {},
}) => {
  const firstMount = useIsMount();
  const { edittedFields, addNewEdittedField } = useContext(ConfiguratorContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [firstRangeFee, setFirstRangeFee] = useState("");
  const [firstRangeFeeErrorMessage, setFirstRangeFeeErrorMessage] =
    useState("");
  const [secondRangeFee, setSecondRangeFee] = useState("");
  const [secondRangeFeeErrorMessage, setSecondRangeFeeErrorMessage] =
    useState("");
  const [thirdRangeFee, setThirdRangeFee] = useState("");
  const [thirdRangeFeeErrorMessage, setThirdRangeFeeErrorMessage] =
    useState("");

  const [firstRange, setFirstRange] = useState({
    value: 1,
    options: createDaysDropdownValues(1, 28, "client-config-first-range-day"),
  });
  const [secondRange, setSecondRange] = useState({
    value: 14,
    options: createDaysDropdownValues(1, 28, "client-config-second-range-day"),
  });
  const [thirdRange, setThirdRange] = useState({
    value: 21,
    options: createDaysDropdownValues(1, 28, "client-config-third-range-day"),
  });

  const [fromConfig, setFromConfig] = useState(true);

  useEffect(() => {
    const dateField = edittedFields.find((item) => item.key === elementKey);
    if (dateField) {
      if (dateField.value) {
        const dateValues = dateField.value.split(";");
        setFromConfig(true);
        setNumberOfDateRanges(dateValues.length);
        setInitialRangeValues(dateValues.length, dateValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!firstMount) {
      if (!fromConfig) {
        setInitialRangeValues(numberOfDateRanges, []);
      }
      if (fromConfig) {
        setFromConfig(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfDateRanges]);

  useEffect(() => {
    if (!fromConfig) {
      if (firstRange.value) {
        const newSecondRangeDateStart = +firstRange.value + 1;
        let newSecondRangeDateEnd = 28;
        if (thirdRange.value) {
          newSecondRangeDateEnd = +thirdRange.value - 1;
        }
        setSecondRange({
          ...secondRange,
          options: createDaysDropdownValues(
            newSecondRangeDateStart,
            newSecondRangeDateEnd,
            "client-config-second-range-day",
          ),
        });
        if (!secondRange.value) {
          setThirdRange({
            ...thirdRange,
            options: createDaysDropdownValues(
              newSecondRangeDateStart,
              28,
              "client-config-third-range-day",
            ),
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRange.value]);

  useEffect(() => {
    if (!fromConfig) {
      if (secondRange.value) {
        setFirstRange({
          ...firstRange,
          options: createDaysDropdownValues(
            1,
            +secondRange.value - 1,
            "client-config-first-range-day",
          ),
        });
        setThirdRange({
          ...thirdRange,
          options: createDaysDropdownValues(
            +secondRange.value + 1,
            28,
            "client-config-third-range-day",
          ),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondRange.value]);

  useEffect(() => {
    if (!fromConfig) {
      if (thirdRange.value) {
        const newSecondRangeDateEnd = +thirdRange.value - 1;
        let newSecondRangeDateStart = 1;
        if (firstRange.value) {
          newSecondRangeDateStart = +firstRange.value + 1;
        }
        setSecondRange({
          ...secondRange,
          options: createDaysDropdownValues(
            newSecondRangeDateStart,
            newSecondRangeDateEnd,
            "client-config-second-range-day",
          ),
        });
        if (!secondRange.value) {
          setFirstRange({
            ...firstRange,
            options: createDaysDropdownValues(
              1,
              newSecondRangeDateEnd,
              "client-config-first-range-day",
            ),
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdRange.value]);

  useEffect(() => {
    if (!fromConfig) {
      let value = "";
      if (
        firstRangeFeeErrorMessage ||
        secondRangeFeeErrorMessage ||
        thirdRangeFeeErrorMessage ||
        numberOfDateRanges === 0 ||
        (numberOfDateRanges > 1 && !secondRange.value) ||
        (numberOfDateRanges > 2 && !thirdRange.value)
      ) {
        value = "";
      } else {
        if (numberOfDateRanges > 0) {
          value += `${firstRange.value}-${
            numberOfDateRanges === 1 ? LAST_RANGE_END_DAY : secondRange.value
          }=${firstRangeFee.length > 0 ? +firstRangeFee : 0}${
            numberOfDateRanges === 1 ? "" : ";"
          }`;
        }
        if (numberOfDateRanges > 1) {
          value += `${secondRange.value + 1}-${
            numberOfDateRanges === 2 ? LAST_RANGE_END_DAY : thirdRange.value
          }=${secondRangeFee.length > 0 ? +secondRangeFee : 0}${
            numberOfDateRanges === 2 ? "" : ";"
          }`;
        }
        if (numberOfDateRanges > 2) {
          value += `${thirdRange.value + 1}-${LAST_RANGE_END_DAY}=${
            thirdRangeFee.length > 0 ? +thirdRangeFee : 0
          }`;
        }
      }
      addNewEdittedField({
        key: elementKey,
        value: value,
        modified: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    numberOfDateRanges,
    firstRange.value,
    secondRange.value,
    thirdRange.value,
    firstRangeFee,
    secondRangeFee,
    thirdRangeFee,
    firstRangeFeeErrorMessage,
    secondRangeFeeErrorMessage,
    thirdRangeFeeErrorMessage,
  ]);

  const setFirstRangeValue = (value) => {
    setFirstRange({ ...firstRange, value });
  };

  const setSecondRangeValue = (value) => {
    setSecondRange({ ...secondRange, value });
  };

  const setThirdRangeValue = (value) => {
    setThirdRange({ ...thirdRange, value });
  };

  const setInitialRangeValues = (dateRanges, config) => {
    let firstDate = 1;
    let secondDate = 14;
    let thirdDate = 21;
    if (dateRanges === 1) {
      if (config.length > 0) {
        const firstInterval = config[0].split("-");
        const firstRangeFee = firstInterval[1].split("=")[1];
        firstDate = +firstInterval[0];
        setFirstRangeFee(firstRangeFee);
      }
      setFirstRange({
        value: firstDate,
        options: createDaysDropdownValues(
          1,
          28,
          "client-config-first-range-day",
        ),
      });
      setSecondRange({ value: "", options: [] });
      setThirdRange({ value: "", options: [] });
    }
    if (dateRanges === 2) {
      if (config.length > 0) {
        const firstInterval = config[0].split("-");
        const secondInterval = config[1].split("-");
        const firstRangeFee = firstInterval[1].split("=")[1];
        const secondRangeFee = secondInterval[1].split("=")[1];
        firstDate = +firstInterval[0];
        secondDate = +firstInterval[1].split("=")[0];
        setFirstRangeFee(firstRangeFee);
        setSecondRangeFee(secondRangeFee);
      }
      setFirstRange({
        value: firstDate,
        options: createDaysDropdownValues(
          1,
          secondDate - 1,
          "client-config-first-range-day",
        ),
      });
      setSecondRange({
        value: secondDate,
        options: createDaysDropdownValues(
          firstDate + 1,
          28,
          "client-config-second-range-day",
        ),
      });
      setThirdRange({ value: "", options: [] });
    }
    if (dateRanges === 3) {
      if (config.length > 0) {
        const firstInterval = config[0].split("-");
        const secondInterval = config[1].split("-");
        const thirdInterval = config[2].split("-");
        const firstRangeFee = firstInterval[1].split("=")[1];
        const secondRangeFee = secondInterval[1].split("=")[1];
        const thirdRangeFee = thirdInterval[1].split("=")[1];
        firstDate = +firstInterval[0];
        secondDate = +firstInterval[1].split("=")[0];
        thirdDate = +secondInterval[1].split("=")[0];
        setFirstRangeFee(firstRangeFee);
        setSecondRangeFee(secondRangeFee);
        setThirdRangeFee(thirdRangeFee);
      }
      setFirstRange({
        value: firstDate,
        options: createDaysDropdownValues(
          1,
          secondDate - 1,
          "client-config-first-range-day",
        ),
      });
      setSecondRange({
        value: secondDate,
        options: createDaysDropdownValues(
          firstDate + 1,
          thirdDate - 1,
          "client-config-second-range-day",
        ),
      });
      setThirdRange({
        value: thirdDate,
        options: createDaysDropdownValues(
          secondDate + 1,
          28,
          "client-config-third-range-day",
        ),
      });
    }
  };

  return (
    <>
      <Counter
        counter={numberOfDateRanges}
        setCounter={setNumberOfDateRanges}
        minValue={0}
        maxValue={3}
        width="100%"
      ></Counter>
      {numberOfDateRanges > 0 ? (
        <>
          <FlexContainer
            flexdirection="column"
            width="100%"
            alignitems="flex-start"
            gap="14px"
            borderradius="0"
            sx={{
              overflow: "hidden",
              // Animating an explicit height means guessing how tall the rows are,
              // and anything taller than the guess is silently clipped. A generous
              // max-height animates the same way but always shows the whole drawer.
              // The unit matters: sx puts maxHeight through MUI's sizing transform,
              // which turns a bare "0" into "0%", and a percentage against this
              // auto-height parent resolves to none, so the drawer never collapsed.
              maxHeight: drawerOpen ? DRAWER_MAX_HEIGHT : "0px",
              transition: "max-height 0.3s ease-in-out",
            }}
          >
            {/* Days and amount share a row, so the narrow amount box is what keeps
                the whole range on one line inside the card. */}
            <FlexContainer width="100%" gap="6px">
              <FlexContainer
                gap="5px"
                width={DAYS_COLUMN_WIDTH}
                flexshrink="0"
                justifycontent="flex-start"
              >
                <Typography
                  color={selected ? "primary" : "text.hint"}
                  fontWeight="bold"
                >
                  Days
                </Typography>
                <FormItemDropdown
                  isSmall
                  elementWidth={DAY_DROPDOWN_WIDTH}
                  value={firstRange.value}
                  setValue={setFirstRangeValue}
                  options={firstRange.options}
                  keyAttribute="key"
                  valueAttribute="value"
                  labelAttribute="label"
                />
                {numberOfDateRanges > 1 ? (
                  <>
                    <Typography
                      color={selected ? "primary" : "text.hint"}
                      fontWeight="bold"
                    >
                      to
                    </Typography>
                    <FormItemDropdown
                      isSmall
                      elementWidth={DAY_DROPDOWN_WIDTH}
                      value={secondRange.value}
                      setValue={setSecondRangeValue}
                      options={secondRange.options}
                      keyAttribute="key"
                      valueAttribute="value"
                      labelAttribute="label"
                    />
                  </>
                ) : (
                  <Typography
                    color={selected ? "primary" : "text.hint"}
                    fontWeight="bold"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    to end of month
                  </Typography>
                )}
              </FlexContainer>
              <Typography
                color={selected ? "primary" : "text.hint"}
                fontWeight="bold"
              >
                :
              </Typography>
              <FormItemInput
                elementWidth={FEE_INPUT_WIDTH}
                value={firstRangeFee}
                setValue={(e) =>
                  onChangeHandler(
                    e,
                    setFirstRangeFee,
                    setFirstRangeFeeErrorMessage,
                  )
                }
                placeholder="0"
                blurHandler={(e) =>
                  validateInputValue(e, setFirstRangeFeeErrorMessage)
                }
                blurOnWheel
                adornment
                adornmentPosition="start"
                adornmentSymbol="$"
                inputProps={{
                  step: 0.01,
                  min: 0,
                  type: "number",
                  style: {
                    textAlign: "end",
                  },
                }}
                errorMessage={firstRangeFeeErrorMessage}
                disabled={
                  !firstRange.value ||
                  (numberOfDateRanges === 2 && !secondRange.value)
                }
              />
            </FlexContainer>
            {numberOfDateRanges > 1 ? (
              <FlexContainer width="100%" gap="6px">
                <FlexContainer
                  gap="5px"
                  width={DAYS_COLUMN_WIDTH}
                  flexshrink="0"
                  justifycontent="flex-start"
                >
                  <Typography
                    color={selected ? "primary" : "text.hint"}
                    fontWeight="bold"
                  >
                    Days
                  </Typography>
                  {secondRange.value ? (
                    <Typography
                      color={selected ? "primary" : "text.hint"}
                      fontWeight="bold"
                    >
                      {secondRange.value + 1}
                    </Typography>
                  ) : (
                    <FormItemDropdown
                      isSmall
                      elementWidth={DAY_DROPDOWN_WIDTH}
                      value={secondRange.value}
                      setValue={setSecondRangeValue}
                      options={secondRange.options}
                      keyAttribute="key"
                      valueAttribute="value"
                      labelAttribute="label"
                    />
                  )}
                  {numberOfDateRanges > 2 ? (
                    <>
                      <Typography
                        color={selected ? "primary" : "text.hint"}
                        fontWeight="bold"
                      >
                        to
                      </Typography>
                      <FormItemDropdown
                        isSmall
                        elementWidth={DAY_DROPDOWN_WIDTH}
                        value={thirdRange.value}
                        setValue={setThirdRangeValue}
                        options={thirdRange.options}
                        keyAttribute="key"
                        valueAttribute="value"
                        labelAttribute="label"
                      />
                    </>
                  ) : (
                    <Typography
                      color={selected ? "primary" : "text.hint"}
                      fontWeight="bold"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      to end of month
                    </Typography>
                  )}
                </FlexContainer>
                <Typography
                  color={selected ? "primary" : "text.hint"}
                  fontWeight="bold"
                >
                  :
                </Typography>
                <FormItemInput
                  elementWidth={FEE_INPUT_WIDTH}
                  value={secondRangeFee}
                  setValue={(e) =>
                    onChangeHandler(
                      e,
                      setSecondRangeFee,
                      setSecondRangeFeeErrorMessage,
                    )
                  }
                  placeholder="0"
                  blurHandler={(e) =>
                    validateInputValue(e, setSecondRangeFeeErrorMessage)
                  }
                  blurOnWheel
                  adornment
                  adornmentPosition="start"
                  adornmentSymbol="$"
                  inputProps={{
                    step: 0.01,
                    min: 0,
                    type: "number",
                    style: {
                      textAlign: "end",
                    },
                  }}
                  errorMessage={secondRangeFeeErrorMessage}
                  disabled={
                    !secondRange.value ||
                    (numberOfDateRanges === 3 && !thirdRange.value)
                  }
                />
              </FlexContainer>
            ) : (
              ""
            )}
            {numberOfDateRanges === 3 ? (
              <FlexContainer width="100%" gap="6px">
                <FlexContainer
                  gap="5px"
                  width={DAYS_COLUMN_WIDTH}
                  flexshrink="0"
                  justifycontent="flex-start"
                >
                  <Typography
                    color={selected ? "primary" : "text.hint"}
                    fontWeight="bold"
                  >
                    Days
                  </Typography>
                  {thirdRange.value ? (
                    <Typography
                      color={selected ? "primary" : "text.hint"}
                      fontWeight="bold"
                    >
                      {thirdRange.value + 1}
                    </Typography>
                  ) : (
                    <FormItemDropdown
                      isSmall
                      elementWidth={DAY_DROPDOWN_WIDTH}
                      value={thirdRange.value}
                      setValue={setThirdRangeValue}
                      options={thirdRange.options}
                      keyAttribute="key"
                      valueAttribute="value"
                      labelAttribute="label"
                    />
                  )}
                  <Typography
                    color={selected ? "primary" : "text.hint"}
                    fontWeight="bold"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    to end of month
                  </Typography>
                </FlexContainer>
                <Typography
                  color={selected ? "primary" : "text.hint"}
                  fontWeight="bold"
                >
                  :
                </Typography>
                <FormItemInput
                  elementWidth={FEE_INPUT_WIDTH}
                  value={thirdRangeFee}
                  setValue={(e) =>
                    onChangeHandler(
                      e,
                      setThirdRangeFee,
                      setThirdRangeFeeErrorMessage,
                    )
                  }
                  placeholder="0"
                  blurHandler={(e) =>
                    validateInputValue(e, setThirdRangeFeeErrorMessage)
                  }
                  blurOnWheel
                  adornment
                  adornmentPosition="start"
                  adornmentSymbol="$"
                  inputProps={{
                    step: 0.01,
                    min: 0,
                    type: "number",
                    style: {
                      textAlign: "end",
                    },
                  }}
                  errorMessage={thirdRangeFeeErrorMessage}
                  disabled={!thirdRange.value}
                />
              </FlexContainer>
            ) : (
              ""
            )}
            <Typography
              variant="caption"
              color={selected ? "primary" : "text.hint"}
              sx={{ textAlign: "left" }}
            >
              Day 1 is the first day after the due date. Payments made on or
              before the due date are not charged a fee.
            </Typography>
          </FlexContainer>
          <Button
            fullWidth
            onClick={(e) => setDrawerOpen(!drawerOpen)}
            sx={{ alignSelf: "center" }}
          >
            {drawerOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Button>
        </>
      ) : (
        ""
      )}
    </>
  );
};

export default DateRangeFeeOption;
